import { useState, useEffect } from 'react';
import { ShoppingBag, AlertTriangle, Plus } from 'lucide-react';
import { Link } from 'react-router-dom';
import Modal from '../../../components/ui/Modal';
import Button from '../../../components/ui/Button';
import { usePets } from '../../../contexts/PetsContext';
import { useToast } from '../../../contexts/ToastContext';
import { formatGrams } from '../../../lib/format';
import { OPEN_BAG_MODES } from '../../../config/constants';

/**
 * "Abrir nueva bolsa": mueve una bolsa de la reserva a mainStock.
 *
 * Pregunta el motivo (modo):
 *  - "La anterior se vació" → reemplaza mainStock.
 *  - "Sumar al stock que quedaba" → suma al actual.
 *
 * Casos especiales:
 *  - extraBagsCount === 0 → muestra empty state con CTA "Compré bolsas".
 *  - bagCapacityGr null → no debería pasar si extraBagsCount > 0
 *    (porque addExtraBags exige la primera vez), pero defendemos por las dudas.
 */
const OpenBagModal = ({ group, isOpen, onClose, onSuccess, onBuyBagsClick }) => {
  const { openBag } = usePets();
  const toast = useToast();

  const [mode, setMode] = useState(OPEN_BAG_MODES.PREVIOUS_EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      // Default razonable: si quedaba stock en main, presumimos que el usuario
      // está topándose por anticipación → ADD_TO_CURRENT. Si no, PREVIOUS_EMPTY.
      setMode(group.mainStockGr > 0 ? OPEN_BAG_MODES.ADD_TO_CURRENT : OPEN_BAG_MODES.PREVIOUS_EMPTY);
      setError(null);
    }
  }, [isOpen, group.mainStockGr]);

  // Empty state: sin bolsas en reserva.
  if ((group.extraBagsCount ?? 0) === 0) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title="Sin bolsas en reserva">
        <div className="flex flex-col gap-4">
          <p className="text-sm text-slate-600">
            La reserva de <strong>{group.name}</strong> está en 0. Registrá las bolsas
            que tengas guardadas para poder abrirlas cuando se vacíe la principal.
          </p>
          <div className="flex gap-2">
            <Button variant="secondary" fullWidth onClick={onClose}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              fullWidth
              leftIcon={Plus}
              onClick={() => {
                onClose();
                onBuyBagsClick?.();
              }}
            >
              Compré bolsas
            </Button>
          </div>
        </div>
      </Modal>
    );
  }

  if (group.bagCapacityGr == null) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title="Falta configurar la bolsa">
        <div className="flex flex-col gap-4">
          <p className="text-sm text-slate-600">
            No tenemos registrada la capacidad de las bolsas cerradas. Registrá
            primero el tamaño desde "Compré bolsas".
          </p>
          <Button variant="primary" fullWidth onClick={onClose}>
            Entendido
          </Button>
        </div>
      </Modal>
    );
  }

  const bagCap = group.bagCapacityGr;
  const previewMain = mode === OPEN_BAG_MODES.PREVIOUS_EMPTY
    ? bagCap
    : group.mainStockGr + bagCap;
  const discardedGr = mode === OPEN_BAG_MODES.PREVIOUS_EMPTY ? group.mainStockGr : 0;

  const handleSubmit = async (e) => {
    e?.preventDefault();
    try {
      setSubmitting(true);
      setError(null);
      await openBag(group.id, { mode });
      toast.success(`Abriste una bolsa en ${group.name}. Quedan ${(group.extraBagsCount ?? 0) - 1} en reserva.`);
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err.message || 'No se pudo abrir la bolsa.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={() => !submitting && onClose()} title="Abrir bolsa nueva">
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div className="rounded-lg bg-slate-50 px-3 py-2.5 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-slate-500">Bolsa principal actual</span>
            <span className="font-medium text-slate-900">{formatGrams(group.mainStockGr)}</span>
          </div>
          <div className="mt-1.5 flex items-center justify-between">
            <span className="text-slate-500">Bolsa cerrada</span>
            <span className="font-medium text-slate-900">{formatGrams(bagCap)}</span>
          </div>
          <div className="mt-1.5 flex items-center justify-between">
            <span className="text-slate-500">En reserva</span>
            <span className="font-medium text-slate-900">
              {group.extraBagsCount} bolsa{group.extraBagsCount === 1 ? '' : 's'}
            </span>
          </div>
        </div>

        <div>
          <p className="text-sm font-medium text-slate-700 mb-2">¿Por qué abrís una bolsa?</p>
          <div className="flex flex-col gap-2">
            <button
              type="button"
              onClick={() => setMode(OPEN_BAG_MODES.PREVIOUS_EMPTY)}
              disabled={submitting}
              className={[
                'flex items-center justify-between gap-3 rounded-xl border px-3 py-2.5 text-left transition-colors',
                mode === OPEN_BAG_MODES.PREVIOUS_EMPTY
                  ? 'border-brand-500 bg-brand-50'
                  : 'border-slate-200 bg-white hover:bg-slate-50',
              ].join(' ')}
            >
              <span className="flex-1">
                <span className="block text-sm font-medium text-slate-900">
                  La anterior se vació
                </span>
                <span className="block text-xs text-slate-500 mt-0.5">
                  La principal queda en {formatGrams(bagCap)}.
                </span>
              </span>
              <span
                aria-hidden="true"
                className={[
                  'h-4 w-4 rounded-full shrink-0',
                  mode === OPEN_BAG_MODES.PREVIOUS_EMPTY ? 'bg-brand-600' : 'border-2 border-slate-300',
                ].join(' ')}
              />
            </button>
            <button
              type="button"
              onClick={() => setMode(OPEN_BAG_MODES.ADD_TO_CURRENT)}
              disabled={submitting}
              className={[
                'flex items-center justify-between gap-3 rounded-xl border px-3 py-2.5 text-left transition-colors',
                mode === OPEN_BAG_MODES.ADD_TO_CURRENT
                  ? 'border-brand-500 bg-brand-50'
                  : 'border-slate-200 bg-white hover:bg-slate-50',
              ].join(' ')}
            >
              <span className="flex-1">
                <span className="block text-sm font-medium text-slate-900">
                  Sumar al stock que quedaba
                </span>
                <span className="block text-xs text-slate-500 mt-0.5">
                  La principal queda en {formatGrams(group.mainStockGr + bagCap)}.
                </span>
              </span>
              <span
                aria-hidden="true"
                className={[
                  'h-4 w-4 rounded-full shrink-0',
                  mode === OPEN_BAG_MODES.ADD_TO_CURRENT ? 'bg-brand-600' : 'border-2 border-slate-300',
                ].join(' ')}
              />
            </button>
          </div>
        </div>

        {discardedGr > 0 && (
          <div className="flex items-start gap-2.5 rounded-lg bg-amber-50 px-3 py-2.5 ring-1 ring-amber-100">
            <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600 mt-0.5" aria-hidden="true" />
            <p className="text-xs text-amber-900">
              El sistema creía que la bolsa principal tenía {formatGrams(discardedGr)}.
              Esa cantidad se descarta como discrepancia (manda lo que viste vos).
            </p>
          </div>
        )}

        <div className="rounded-lg bg-brand-50 px-3 py-2 text-sm text-brand-800">
          Resultado: bolsa principal <strong>{formatGrams(previewMain)}</strong> ·
          reserva queda en <strong>{group.extraBagsCount - 1}</strong>.
        </div>

        {error && (
          <p className="text-sm text-status-red bg-red-50 rounded-lg px-3 py-2">{error}</p>
        )}

        <div className="flex gap-2">
          <Button variant="secondary" fullWidth onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="primary"
            fullWidth
            leftIcon={ShoppingBag}
            isLoading={submitting}
          >
            Abrir bolsa
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default OpenBagModal;
